// Lazily injects the Google Maps JavaScript API and resolves once `window.google`
// is available. The script is added at most once per page load; concurrent
// callers share the same promise. The API key comes from the build-time env var
// VUE_APP_GOOGLE_MAPS_API_KEY.

let loadPromise = null;

// `loading=async` returns a bootstrap that synchronously defines an empty
// `google.maps` stub before fetching the real API, so neither the script's
// `load` event nor the presence of `google.maps` means the API is usable.
// Resolve on the `callback` parameter instead, and probe for a real constructor.
const CALLBACK = '__initGoogleMaps';

export function loadGoogleMaps() {
    if (window.google && window.google.maps && window.google.maps.Map) {
        return Promise.resolve(window.google);
    }
    if (loadPromise) return loadPromise;

    const key = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;
    if (!key) {
        return Promise.reject(new Error('Missing VUE_APP_GOOGLE_MAPS_API_KEY'));
    }

    loadPromise = new Promise((resolve, reject) => {
        window[CALLBACK] = () => {
            delete window[CALLBACK];
            resolve(window.google);
        };
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}`
            + `&loading=async&callback=${CALLBACK}`;
        script.async = true;
        script.onerror = () => {
            delete window[CALLBACK];
            loadPromise = null; // allow a retry on a later mount
            reject(new Error('Failed to load Google Maps script'));
        };
        document.head.appendChild(script);
    });

    return loadPromise;
}
